import { CloudActions, Product } from '@portal/portal-core';
import { SuperAdminActions } from 'ducks/super-client';
import { parseDate } from 'utils';

// Date-like values accepted by parseDate (string | number | Date | null).
type DateLike = string | number | Date | null | undefined;

// Minimal shape of the license-creation form state accessed here. All fields are
// optional because callers (and tests) may build the action from a partial state.
interface CreateLicenseState {
  productName?: string;
  adminID?: string;
  costCenterName?: string;
  costCenterID?: string;
  whitelistSuffixes?: unknown;
  country?: string;
  productAccessType?: string;
  licenseStartDate?: DateLike;
  licenseEndDate?: DateLike;
  maxSharedSeatCount?: string | number;
  seatCount?: string | number;
  whitelistIgnore?: unknown;
  admins?: unknown;
  enabledFeatures?: Record<string, unknown>;
}

type CreateLicenseActionResult = ReturnType<typeof SuperAdminActions.createLicense>;

const buildAction = (
  state: CreateLicenseState,
  institutional: boolean | undefined,
  existingLicensesInfo?: unknown,
): CreateLicenseActionResult => {
  return SuperAdminActions.createLicense({
    institutional,
    productName: state.productName,
    ownerID: state.adminID,
    costCenterName: state.costCenterName,
    costCenterID: state.costCenterID,
    whitelistSuffixes: state.whitelistSuffixes,
    countryCode: state.country,
    productAccessType: state.productAccessType,
    startDate: parseDate(state.licenseStartDate as string | number | Date | null),
    endDate: parseDate(state.licenseEndDate as string | number | Date | null),
    maxSharedComputers:
      institutional && state.maxSharedSeatCount ? Number(state.maxSharedSeatCount) : 0,
    seatCount: Number(state.seatCount),
    whitelistIgnore: state.whitelistIgnore,
    admins: state.admins,
    existingLicensesInfo,
    enabledFeatures: state.enabledFeatures || {},
  });
};

interface CreateUserAndLicenseInput {
  productName?: string;
  productAccessType?: string;
  email?: string;
  country?: string;
  firstName?: string;
  lastName?: string;
  licenseEndDate?: DateLike;
}

export const buildCreateUserAndLicenseAction = ({
  productName,
  productAccessType,
  email: primaryEmail,
  country: costCenterCountryCode,
  firstName,
  lastName,
  licenseEndDate: endDate,
}: CreateUserAndLicenseInput): ReturnType<
  typeof CloudActions.adminAddPhantomUserToProduct
> | false => {
  const args = [
    productName,
    productAccessType,
    primaryEmail,
    costCenterCountryCode,
    firstName,
    lastName,
    parseDate(endDate as string | number | Date | null),
  ] as const;
  const licensedProductName = Object.values(Product.NAMES_LICENSED_PRODUCTS).includes(
    productName as string,
  );
  if (productName && licensedProductName) {
    return CloudActions.adminAddPhantomUserToProduct(...args);
  }
  return false;
};

export const buildCreateLicenseAction = (
  state: CreateLicenseState,
  isInstitutional?: boolean,
  existingLicensesInfo?: unknown,
): CreateLicenseActionResult | false => {
  if (
    state.productName &&
    Object.values(Product.NAMES_LICENSED_PRODUCTS).includes(state.productName)
  ) {
    return buildAction(state, isInstitutional, existingLicensesInfo);
  }
  return false;
};
